import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Navigation, Phone, MapPin } from "lucide-react";
import { BUSINESS, IMAGES } from "../data";

const EASE = [0.16, 1, 0.3, 1];

const headline = ["MEGAWIN5K", "Your Gaming", "Destination in Jakarta"];

export const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-16 overflow-hidden"
    >
      {/* background image masked reveal */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 z-0"
        initial={reduce ? { opacity: 0 } : { scale: 1.2, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: EASE }}
      >
        <img
          src={IMAGES.hero}
          alt="Neon-lit gaming environment at MEGAWIN5K game store in Jakarta"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
      </motion.div>

      {/* ambient glows */}
      <div aria-hidden className="absolute -left-20 top-1/3 w-[38rem] h-[38rem] rounded-full bg-neon/20 blur-[120px] animate-pulse-glow z-0" />
      <div aria-hidden className="absolute right-0 bottom-0 w-[30rem] h-[30rem] rounded-full bg-violet/20 blur-[120px] animate-pulse-glow z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
          className="mb-6 inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.2em] uppercase font-bold text-neon"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-8 bg-neon" /> Game Store · Jakarta Selatan, Indonesia
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-5xl"
        >
          {headline.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className={`block ${i === 0 ? "text-neon" : "text-white"}`}
                initial={reduce ? { opacity: 0 } : { y: "110%" }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.35 + i * 0.12 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
          data-testid="hero-subtext"
        >
          A welcoming space built for gaming and entertainment in the heart of
          Jakarta. Come play, unwind, and enjoy the experience at MEGAWIN5K.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.95 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={BUSINESS.directions}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-directions-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-neon text-black font-bold px-8 py-4 hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-shadow duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <Navigation size={18} className="transition-transform duration-300 group-hover:rotate-45" />
            Get Directions
          </a>
          <a
            href={BUSINESS.callLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-call-btn"
            className="inline-flex items-center gap-2 rounded-full bg-transparent border border-white/20 text-white font-bold px-8 py-4 hover:border-neon hover:text-neon transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon"
          >
            <Phone size={18} /> Call Now
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-10 inline-flex items-center gap-2 text-sm text-white/60"
          data-testid="hero-location"
        >
          <MapPin size={16} className="text-neon" />
          {BUSINESS.addressLine}, Indonesia
        </motion.div>
      </div>
    </section>
  );
};
